import React, { useState } from "react";

export interface U2000DeleteHandlersParams<T extends { id: string } = any> {
  getSelectedRows: () => T[];
  doDeleteFn: (id: string, row: T) => Promise<any>;
  onSuccess?: (deletedRows: T[]) => void;
  onError?: (errors: { row: T; error: any }[]) => void;
}

export interface U2000DeleteHandlersReturn<T extends { id: string } = any> {
  handleDelete: () => void;
  isDialogDeleteOpen: boolean;
  setDialogDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeleteCloseDlg: (result: string) => void;
  disableDeleteBtn: () => boolean;
  selectedRows: () => T[];
}

export function useU2000DeleteHandlers<T extends { id: string } = any>({ getSelectedRows, doDeleteFn, onSuccess, onError }: U2000DeleteHandlersParams<T>): U2000DeleteHandlersReturn<T> {
  const [isDialogDeleteOpen, setDialogDeleteOpen] = useState<boolean>(false);

  const handleDelete = () => {
    if (!getSelectedRows || getSelectedRows().length === 0) {
      return;
    }
    setDialogDeleteOpen(true);
  };

  const handleDeleteCloseDlg = (result: string) => {
    if (result === "yes") {
      onDelete(getSelectedRows());
    }
    setDialogDeleteOpen(false);
  };

  const selectedRows = () => getSelectedRows();

  async function onDelete(deletedRows: T[]): Promise<{ successes: T[]; errors: { row: T; error: any }[] }> {
    if (!deletedRows || deletedRows.length === 0) {
      return { successes: [], errors: [] };
    }

    const results = await Promise.all(
      deletedRows.map(async (deleteRow) => {
        try {
          await doDeleteFn(deleteRow.id, deleteRow);
          return { success: true, row: deleteRow };
        } catch (error) {
          return { success: false, row: deleteRow, error };
        }
      })
    );

    const successes = results.filter((r) => r.success).map((r) => r.row);
    const errors = results.filter((r) => !r.success).map((r) => ({ row: r.row, error: r.error }));

    if (successes.length > 0 && onSuccess) {
      onSuccess(successes);
    }
    if (errors.length > 0 && onError) {
      onError(errors);
    }

    return { successes, errors };
  }

  const disableDeleteBtn = () => getSelectedRows().length === 0;

  return { handleDelete, isDialogDeleteOpen, setDialogDeleteOpen, handleDeleteCloseDlg, disableDeleteBtn, selectedRows };
}
